'use client';

import Image, { ImageProps } from 'next/image';
import { useState, useEffect, useRef } from 'react';
import styles from './image-with-fallback.module.css';

interface ImageWithFallbackProps extends Omit<ImageProps, 'onError' | 'onLoadingComplete'> {
  fallbackText?: string;
  showPlaceholder?: boolean;
  lazy?: boolean;
}

export const ImageWithFallback = ({ 
  src, 
  alt, 
  fallbackText = 'Изображение недоступно',
  showPlaceholder = true,
  lazy = true,
  className,
  ...props 
}: ImageWithFallbackProps) => {
  const [imageError, setImageError] = useState(false);
  const [isVisible, setIsVisible] = useState(!lazy);
  const imgRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!lazy || isVisible) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, [lazy, isVisible]);

  const handleError = () => {
    setImageError(true);
  };

  const handleLoadingComplete = () => {
    setImageError(false);
  };

  if (imageError || !src) {
    if (!showPlaceholder) return null;
    
    return (
      <div ref={imgRef} className={`${styles.placeholder} ${className || ''}`}>
        <div className={styles.fallbackText}>{fallbackText}</div>
      </div>
    );
  }

  if (!isVisible) {
    return (
      <div ref={imgRef} className={`${styles.placeholder} ${className || ''}`}>
        <div className={styles.fallbackText}>Загрузка...</div>
      </div>
    );
  }

  return (
    <Image
      {...props}
      src={src}
      alt={alt}
      className={className}
      loading={lazy ? "lazy" : "eager"}
      onError={handleError}
      onLoadingComplete={handleLoadingComplete}
    />
  );
}; 